/**
 * Module for overriding Backbone's extend logic - providing dependency injection at the very core.
 */
define((require) => {
    "use strict";

    const _               = require("underscore");
    const Backbone        = require("backbone");
    const AssociatedModel = require("backbone.associations"); // required when built/bundled!
    const Injector        = require("shared/util/Injector");

    const {
        Model,
        Collection,
        Router,
        View,
        History
    } = Backbone;

    // Reserved prop names...
    const CTOR       = "constructor";
    const INJECT     = "injection";
    const MIXIN      = "mixin";
    const INSTANCEOF = "instanceOf";
    const ASSIGNABLE = "assignableFrom";
    const SUPER      = "__super";

    // Reserved (fixed) properties not to override during mixin! Note that CTOR is *not* included here as we want
    // it to be present in props mixed in as it is handled specifically!...
    const RESERVED = new Set(["extend", "ctx", "__super__", "__typeName", INJECT, MIXIN, INSTANCEOF, ASSIGNABLE, "__ctor"]); // __ctor used for runtime book keeping!

    // Used to generate unique mixin names where non are supplied, to be able to test for any type...
    let SEQUENCE = 1;

    // The universe of known types, exposed publicly as "universe" on each "ctx" (shared)...
    const UNIVERSE = {};

    // Required because every object has a prototype "constructor" property!
    const prop = (name, props, remove) => { // remove will fx be true for static "injection" property...
        let value;
        if (props && props.hasOwnProperty(name)) {
            value = props[name];
            if (remove) {
                delete props[name];
            }
        }
        return value;
    };

    // Registers a new constructor to "ctx", i.e. mixin constructors *or* the single (parent) one...
    const register = (ctx, props, injection, parent) => {
        const ctor = prop(CTOR, props) || parent;
        if (_.isFunction(ctor)) {
            // Add "constructor" functions to array of such (mixin) constructors used by the actual
            // constructor wrapper stored in "ctx.ctor", in an entry tagged with required injections.
            // Note that we can still inject (as options) if we have defaulted to "parent" constructor...
            const entry = injection ? {ctor, injection} : ctor;
            const constructors = prop(CTOR, ctx);

            if (constructors) {
                constructors.push(entry);
            } else {
                ctx[CTOR] = [entry]; // create "constructors" array
            }

            // Make sure update the "props" reference to the actual constructor function, as it will be
            // added (mixed) with the actual type later...
            props[CTOR] = ctx.ctor;
        }
    };

    // Call constructors(s) with the specified scope ("this"), including recursive parent constructor calls where
    // needed, and with injection support all the way...
    const call = function(self, ctx, args) {
        const called = self.__ctor;
        const constructors = prop(CTOR, ctx);
        const parent = ctx.parent;
        const old = self[SUPER];
        let entry;
        let ctor;
        let params;
        let i;

        // Expose super constructor for (mixin) constructor about to run, defaulting to a noop so it can always be
        // called (always present). This is a bit different from normal override as mixin can cause constructors
        // to be called multiple times which we do not want...
        self[SUPER] = function(...superArgs) {
            if (parent) {
                if (!called.has(parent)) {
                    called.add(parent);
                    // If the parent constructor is one defined by us, "call" will be recursively called and
                    // "__super" will be exposed below; otherwise it could be a Backbone constructor, which
                    // will never use "__super" anyways...
                    parent.apply(self, superArgs.length === 0 ? args : superArgs);
                }
            }
        };

        // Call each (mixin) constructor not yet called...
        for (i = 0; i < constructors.length; i++) {
            entry = constructors[i];

            // If not a constructor function, it will be an entry like {ctor: Function, injection: *}...
            if (_.isFunction(entry)) {
                params = args;
                ctor   = entry;
            } else {
                params = Injector.getParameters(entry.injection, args); // does not clobber "args"!
                ctor   = entry.ctor;
            }

            // Has our constructor already run for scope (this)?
            if (!called.has(ctor)) {
                // Record this this constructor has (will) now run, so it will be ignored on future calls, such
                // as multiple call super calls from different mixins...
                called.add(ctor);
                // Call constructor, which will call exposed super constructor when needed...
                ctor.apply(self, params);
            }
        }

        // Remove super constructor, since this constructor has run...
        if (old) {
            self[SUPER] = old;
        } else {
            delete self[SUPER];
        }
    };

    // Wraps the supplied mixin "fn" for the named property if a super function is available at call (mixin) time,
    // making a "__super(..)" reference available at call time. If no super function is available, or if "fn"
    // does not call __super(..)", "fn" is simply returned. However, if "fn" calls "__super()", we must make sure
    // we always have a super function even if no actual super function; if so, "__super()" is just a no-op at
    // call time...
    const override = function(parent, name, fn, superFn) {
        // If no "__super(..)" reference in the "fn" code, simply use "fn" as-is...
        if (fn.toString().indexOf(`.${SUPER}(`) !== -1) {
            // make sure we match invocations, not __super__!

            // Get a reference to the super function at mixin time, since the super reference may change over time...
            superFn = superFn || (parent && parent[name]);

            // Since "fn" is known to call "__super()", we must make sure it is present. If we have no actual
            // super function, we create a no-op on the fly to use...
            if (!_.isFunction(superFn)) {
                superFn = function() {}; // no-op
            }

            // Qualify function name for super call...
            const typeName = parent && parent.__typeName;
            if (typeName) {
                name = `${typeName}.${name}`;
            }

            // Wrap "fn" with a function that first defines "__super()" primed with a reference to the actual
            // super function at mixin time, making it available to be called with the same args or explicitly
            // added ones at runtime...
            return function(...args) {
                const self = this;
                const old = self[SUPER];

                // Expose "__super()" for "self" at runtime...
                self[SUPER] = function(...superArgs) {
                    return superFn.apply(self, superArgs.length ? superArgs : args);
                };

                // Call actual mixin function, which may end up calling "__super(..)" then. Notice if "__super()"
                // fails, our finally block will be reached below, making sure we clean up in any case...
                try {
                    return fn.apply(self, args);
                } finally {
                    self[SUPER] = old; // TODO: can this be corrupted on recursion?
                }
            };
        }

        // Return as is, we have no reference to a super function to call...
        return fn;
    };

    // Extend "Type" prototype with all the supplied proto props, allowing functions to reference "__super(..)" to
    // easily access super function, if any...
    const def = (Type, props) => { // "props" already defaulted here!
        _.each(props, (prop, name) => {
            if (_.isFunction(prop) && (name !== CTOR)) { // constructor already handled
                prop = override(Type.__super__, name, prop);
            }
            Type.prototype[name] = prop; // TODO: should auto extend props etc like on mixin, but with diff semantics
        });
    };

    // "mixin" is different from "extend": functions will still override, but property maps and arrays
    // should be merged (mixed) with such existing properties, if any, otherwise created. We do also
    // support recursive mixin via a mixin defining a "mixin" property (handled in "mixin(..)"...
    const mix = (self, target, props) => {
        _.each(props, (mixin, name) => {
            const existing = target[name];
            let mix = true;
            // Have to check this because _.isObject(someFunction) returns true even if
            // _.isFunction(someFunction) does as well!...
            if (_.isFunction(mixin)) {
                // We can mix stuff like a function returning defaults into a map of existing "defaults"; we
                // do not support the reverse just yet (not needed). We assume "existing" has some sensible
                // value...
                if (!_.isFunction(existing) && _.isObject(existing)) {
                    const fn = mixin.bind(self);
                    mixin = function() {
                        return _.extend(existing, fn());
                    };
                    // Fall-through!

                // Do not wrap constructors, already handled as they can/must only be invoked once...
                } else if (name !== CTOR) {
                    // Override mixin functions to make their super function available...
                    mixin = override(self.__super__, name, mixin, existing);
                    // Fall-through!
                }
                // Always mix, even "constructor"...

            // Handle array properties: we *cannot* change the "existing" array, or we will/may change
            // the entire prototype chain further up, which is really less than desirable...
            } else if (_.isArray(mixin)) {
                if (_.isArray(existing)) {
                    mixin = existing.concat(mixin);
                // eslint-disable-next-line eqeqeq
                } else if (existing != null) { // Allow single non-null/non-defined prop to become array...
                    mixin.unshift(existing);
                }

            // Handle maps of properties...
            } else if (_.isObject(mixin)) { // TODO: maps!
                if (_.isObject(existing)) {
                    _.extend(existing, mixin); // TODO: reverse, no? Same issue as for arrays...!
                    mix = false; // already set on target!
                }
            } // else some other property we override, like a simple prop...

            if (mix) {
                target[name] = mixin;
            }
        });
    };

    // Mixin properties declared as part of another type declaration/mixin...
    const mixProps = function(Type, mixins, mixinName) {
        if (mixins) {
            // Either a single mixin or an array of such mixins...
            _.each(_.isArray(mixins) ? mixins : [mixins], (mixin) => {
                let mixinProps = mixin.prototype;
                let staticMixinProps = mixin;
                if (!mixinProps) {
                    mixinProps = staticMixinProps;
                    staticMixinProps = undefined;
                }
                if (mixinName) { // if supplied, used as recursive prefix
                    Type.mixin(mixinName, mixinProps, staticMixinProps, true);
                } else {
                    Type.mixin(mixinProps, staticMixinProps);
                }
            });
        }
    };

    // Defines a "mixin" function on the constructor stored in "ctx", so other types can mixin functionality later...
    const defineMixin = function(ctx) {
        const Constructor = ctx.ctor; // declared as this for easier debugging, i.e. stack trace like "Constructor.mixin"...

        Constructor.mixin = function(mixinName, mixinProps, staticMixinProps, recursive) {
            const self = this;
            const typeName = self.prototype.__typeName;
            let i;
            let props = {};

            // Mixin name is optional but *always* supplied on recursion...
            if (!_.isString(mixinName)) {
                staticMixinProps = mixinProps;
                mixinProps = mixinName;
                mixinName = typeName ? `${typeName}Mixin` : `Mixin${SEQUENCE++}`;
            } else if (recursive) {
                if (mixinProps.__typeName) {
                    mixinName += `.${mixinProps.__typeName}`;
                } else {
                    mixinName += `.Mixin${SEQUENCE++}`;
                }
            }

            // Make sure we do not mixin the same mixin multiple times as that can cause odd side
            // effects like calling __super() too many times. If so, we simply return the mixin
            // here as already mixed in for the current context...
            for (i in ctx.types) {
                if (ctx.types[i] === mixinProps) {
                    return mixinProps;
                }
            }

            // Mixin injection for *this* mixin constructor?
            const injection = prop(INJECT, staticMixinProps, true); // true: remove "injection" prop if found
            const mixins = prop(MIXIN, mixinProps, true);           // ditto
            const merge = (target, source) => {
                if (source) {
                    let prop;
                    for (prop in source) {
                        if (!RESERVED.has(prop) && source.hasOwnProperty(prop)) {
                            target[prop] = source[prop];
                        }
                    }
                }
                return target;
            };

            // Use both *own* prototype and normal mixin props, where the non-prototype (more specific)
            // props take precedence...
            if (mixinProps.prototype) {
                props = merge(props, mixinProps.prototype); // _.extend({}, mixinProps.prototype, mixinProps);
            }
            props = merge(props, mixinProps);

            // Register mixin constructor, but do *not* default to parent constructor...
            register(ctx, props, injection);

            // Mix in prototype properties, if any, which will be reflected in the entire prototype chain...
            mix(self, self.prototype, props);

            // Now perform mixin recursively for any mixins declared via a "mixin" property...
            mixProps(self, mixins, mixinName); // recursive mixin name prefix

            // Modify *incoming* props to ensure contexts can use individual mixins for type testing...
            mixinProps[ASSIGNABLE] = assignableFrom;

            // Mix in static properties, if any. Since static properties are not set on the prototype, we have to
            // manually update all sub-types of the type already created, on the mixed in static properties will
            // never be reflected there...
            if (staticMixinProps) {
                props = merge({}, staticMixinProps);
                mix(self, self, props);
                // May be empty, i.e. no sub-types...
                for (i = 0; i < ctx.children.length; i++) {
                    mix(self, ctx.children[i], props);
                }
            }

            // Remember actual mixin type so we can test for instanceof...
            ctx.types[mixinName] = mixinProps;
            UNIVERSE[mixinName] = mixinProps;

            // So we can easily test for this mixin later on, including for its type (or name)...
            return mixinProps;
        };
    };

    // Returns an *instance* function "instanceOf" to test for inheritance on the specified scope (this), or
    // added mixin type, on created instances...
    const instanceOf = function(ctx) {
        return function(type) {
            // Normal instance of...
            if (_.isFunction(type) && (this instanceof type)) {
                return true;

            // Type or mixin name?
            } else if (_.isString(type)) {
                return !!ctx.types[type];
            }
            return !!_.find(ctx.types, (t) => type === t);
        };
    };

    // Static type method that does the equivalent of "instanceOf", just with the arguments reversed...
    const assignableFrom = function(target) {
        return target.instanceOf && target.instanceOf(this);
    };

    // Defines a new type, or actually the constructor to create such a new type. The created type (constructor) will
    // besides the static "extend" function also have a static "mixin" function, so other types can mixin functionality
    // later. It also supports injection...
    const defineType = function(parent, typeName, protoProps, staticProps) {
        // Define context to register mixed in constructors for *this* type as well as the actual constructor
        // wrapper, which will call those super/mixin constructors, at "ctx.ctor"...
        const ctx = {
            parent, children: [], ctor: null, types: {}, universe: UNIVERSE
        };
        const injection = prop(INJECT, staticProps, true); // true: remove "injection" prop if found

        // We *always* define a specific constructor this type (for "typeName"), since we must be able
        // to handle both "extend" and "mixin" functionality. That is, we must only mixin on the specified
        // type, not all the way up the prototype chain, though we want to call super constructors
        // during construction...
        const Type = function(...args) {
            // Initialise: make sure a given constructor function is only called once during construction...
            if (this.__ctor) {
                this.__ctors++;
            } else {
                this.__ctor  = new Set();
                this.__ctors = 1;
            }

            // Call...
            call(this, ctx, args);

            // Clean up...
            if (!(--this.__ctors)) {
                delete this.__ctors;
                delete this.__ctor;
            }
        };

        // Generate a unique name for anonymous (non-named) types, including parent hierarchy tree...
        let qualifiedName;
        if (typeName) {
            qualifiedName = typeName;
        } else {
            typeName = `Type${SEQUENCE++}`;
            qualifiedName = typeName;
            if (parent.prototype) { // parent prototype can be undefined, i.e. root type like "Class"
                qualifiedName += ` < ${(parent.__typeName || "?")}`;
            }
        }

        // Add static properties to the constructor function, if supplied, *before* add static functions
        // for it here, or the constructor would for example use the parent "mixin" function - it only took
        // me 1½ days to find this bug... :/
        _.extend(Type, parent, staticProps);

        ctx.ctor = Type;
        Type.ctx = ctx;

        // Make sure the type has an "instanceOf" function to test for types, including mixin ones, also
        // on name...
        Type[ASSIGNABLE] = assignableFrom;
        protoProps[INSTANCEOF] = instanceOf(ctx);

        // To maintain common instanceOf semantics, indexed by (auto-generated, non-qualified) type name...
        ctx.types[typeName] = protoProps;
        if (parent.ctx) {
            _.extend(ctx.types, parent.ctx.types);
        }

        // Tag constructor with type name for easy identification. Created instances will also have
        // a prototype property called "__typeName" as well...
        Type.__typeName = qualifiedName; // "qualifiedName" intentional!

        // Expand universe indexed by qualified name for debugging ease...
        UNIVERSE[qualifiedName] = protoProps;

        // Register "constructor" from "protoProps", or use "parent" constructor. We pass along any "injection"
        // configuration to be applied at runtime (creation time), if any; injection may still be used for
        // "parent" if configured as options...
        register(ctx, protoProps, injection, parent);

        // Define mixin function for the specified constructor, i.e. for "Type", to add additional
        // constructors and initializers at a later time...
        defineMixin(ctx);

        // Return constructor wrapper to create new "Type" instances by some context, to ensure mixins
        // constructors and initializers are called as well...
        return Type;
    };

    /**
     * The following code is a copy of Backbone's #extend with support for a mixin functionality and a
     * user-friendly type name (description). <p>
     *
     * If a type name is supplied as the first (string) argument, it will be set as the prototype property
     * <tt>__typeName</tt>; if no type name is supplied, the <tt>__typeName</tt> attribute is not set.
     *
     * @param {String|*} [typeName]    If a string, the user-friendly type name, for example a model name like
     *                                 "Experience". The  name should be friendly enough as to be presented to the end
     *                                 user as-is, e.g. in a notification error. If not a string, assumed to the the
     *                                 prototype properties for the model. The user friendly class type will be set
     *                                 as the prototype property named "__typeName", and not set if no type name is
     *                                 supplied. Prototype properties are optional.
     * @param {*}        [protoProps]  The prototype properties, or the static properties if <tt>typeName</tt> is not
     *                                 a string (and thus the prototype properties). Both prototype and static
     *                                 properties are optional.
     * @param {*}        [staticProps] The optional static properties if <tt>typeName</tt> is a string.
     */
    const extend = function(typeName, protoProps, staticProps) {
        // Type name is optional...
        if (!_.isString(typeName)) {
            staticProps = protoProps;
            protoProps = typeName;
            typeName = null;
        }

        protoProps = protoProps || {};

        // "staticProps" may also define an "injection" property that will handle injection (and will be ignored
        // as a static prop on the type)...
        const Type = defineType(this, typeName, protoProps, staticProps);

        // Make sure we keep a link to the sub-type just created, so we can make sure mixed in static properties
        // will be reflected in all such sub-types already defined at mixin time. This is needed because static
        // properties are not set on the prototype, but on actual types (functions)...
        if (this.ctx) {
            this.ctx.children.push(Type);
        }

        // Set the prototype chain to inherit from this type (parent), without calling the
        // constructor function of this (parent) type...
        const Surrogate = function() {
            this.constructor = Type;
        };

        Surrogate.prototype = this.prototype;
        Type.prototype = new Surrogate();

        // Add prototype properties (instance properties) to the subclass, if supplied. If so,
        // the properties supplied during construction can be overridden by mixins...
        // Set a convenience property in case the parent"s prototype is needed later...
        Type.__super__ = this.prototype;

        // Add prototype properties (instance properties) to the subclass, if supplied. If so,
        // the properties supplied during construction takes precedence over mixins, i.e. more specific...
        def(Type, protoProps);

        // Apply mixin(s) defined via "protoProps", if any...
        mixProps(Type, prop(MIXIN, protoProps, true));

        // Add *user-friendly* type name...
        if (typeName) {
            Type.prototype.__typeName = typeName;

            // Add a default toString if not explicitly set for child or proto props; cannot test on prototype,
            // since a toString will always be there...
            if (Type.prototype.toString === Object.prototype.toString) {
                Type.prototype.toString = function() {
                    if (_.isFunction(this.distinguishedName)) {
                        try {
                            // Call fail in certain cases if *this* have not been fully initialised yet...
                            return this.distinguishedName();
                        } catch (e) {
                            // Fall-trough
                        }
                    }
                    let s = this.__typeName.replace(/\s+/g, "_");
                    let id;

                    // Append 'id' if present
                    if (_.isFunction(this.get)) {
                        id = this.get("id"); // Model, but not an ImprovedModel as it will have a distinguishedName already including the id (and version)
                    } else if (this.hasOwnProperty("cid")) {
                        id = this.cid; // View
                    }
                    if (id) {
                        s += `[${id}]`;
                    }
                    return s;
                };
            }
        }

        return Type;
    };

    Model.extend = AssociatedModel.extend = Collection.extend = Router.extend = View.extend = History.extend = extend;

    return extend;
});



// WEBPACK FOOTER //
// ./src/shared/zbone/Extend.js